import { useCallback, useMemo, useRef } from "react";
import { useAppDispatch, useAppSelector } from "../../../store";
import { useGetABOEnrichmentDataMutation } from "../../../services/AboEnrichment/AboEnrichmentAPI";
import { KPI_ID } from "../../../services/AboEnrichment/AboEnrichmentAPI.types";
import { addWhatsAppNumbers, markWhatsAppEnrichmentFailed } from "../../../reducers/whatsAppEnrichment";
import type { ActionReportRow } from "../common/ActionReportListView/types";
import { getAffAboId } from "../common/ActionReportListView/utils/actionReportUtils";

export type WhatsAppFetchState = "unfetched" | "fetching" | "fetched" | "failed";

const BATCH_SIZE = 25;

interface UseLazyWhatsAppEnrichmentProps {
  period: string;
  locale: string;
  isoCountryCode: string;
}

export const useLazyWhatsAppEnrichment = ({ period, locale, isoCountryCode }: UseLazyWhatsAppEnrichmentProps) => {
  const dispatch = useAppDispatch();
  const [fetchEnrichmentData] = useGetABOEnrichmentDataMutation();
  const fetchingRef = useRef<Set<string>>(new Set());

  const whatsAppState = useAppSelector((state) => state.whatsAppEnrichment);
  const numbers = useMemo(() => whatsAppState?.numbers ?? {}, [whatsAppState?.numbers]);
  const fetched = useMemo(() => whatsAppState?.fetched ?? [], [whatsAppState?.fetched]);
  const failed = useMemo(() => whatsAppState?.failed ?? [], [whatsAppState?.failed]);

  const getWhatsAppFetchState = useCallback(
    (affAbo: string): WhatsAppFetchState => {
      if (fetchingRef.current.has(affAbo)) return "fetching";
      if (failed.includes(affAbo)) return "failed";
      if (fetched.includes(affAbo)) return "fetched";
      return "unfetched";
    },
    [fetched, failed],
  );

  const getWhatsAppNumber = useCallback(
    (affAbo: string): string | undefined => {
      return numbers[affAbo];
    },
    [numbers],
  );

  const fetchWhatsAppNumbers = useCallback(
    async (rows: ActionReportRow[]) => {
      const affAbosToFetch = rows
        .map((row) => getAffAboId(row))
        .filter((affAbo): affAbo is string => !!affAbo && getWhatsAppFetchState(affAbo) === "unfetched");

      if (affAbosToFetch.length === 0) return;

      for (let i = 0; i < affAbosToFetch.length; i += BATCH_SIZE) {
        const batch = affAbosToFetch.slice(i, i + BATCH_SIZE);
        batch.forEach((affAbo) => fetchingRef.current.add(affAbo));

        try {
          const response = await fetchEnrichmentData({
            affAbos: batch,
            bonusPeriod: period,
            locale,
            isoCountryCode,
            kpis: [KPI_ID.WHATSAPP],
          }).unwrap();

          const numbersByAffAbo = response.aboKpiData.reduce(
            (acc, data) => {
              acc[`${data.aff}-${data.abo}`] = data.whatsapp;
              return acc;
            },
            {} as Record<string, string | undefined>,
          );

          dispatch(addWhatsAppNumbers({ affAbos: batch, numbers: numbersByAffAbo }));
        } catch {
          dispatch(markWhatsAppEnrichmentFailed({ affAbos: batch }));
        } finally {
          batch.forEach((affAbo) => fetchingRef.current.delete(affAbo));
        }
      }
    },
    [getWhatsAppFetchState, dispatch, fetchEnrichmentData, period, locale, isoCountryCode],
  );

  return { fetchWhatsAppNumbers, getWhatsAppNumber, getWhatsAppFetchState };
};
